import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { BaseResponseModel } from '../models/BaseResponseModel';
import { JWTPayloadData } from '../models/newModels/JWTPayloadData';
import { UtilsService } from './utils/utils.service';
import { CookieService } from 'ngx-cookie-service';
import { FacilityData } from '../models/newModels/FacilityData';
import * as moment from 'moment';
import * as jwt_decode from 'jwt-decode';
import { Settings } from '../models/shift-management-models/Settings';
import { UserTypeData } from '../models/newModels/UserTypeData';
import { BillingSummaryModel } from '../models/newModels/BillingSummaryModel';
import { UserTypes } from '../config/user-types';
import { JavaBaseResponse } from '../models/shift-management-models/java-base-response';
import * as braze from "@braze/web-sdk";
import { BrazeService } from './braze.service';


/**
 * ManageUsersAccountService manages the user account.The registration,login,logout.Saves, updates user from local storage and database
 * and removes from ls when user logout-s.
 */

const httpOptions = {
  withCredentials: false,
  observe: 'response' as 'response'
};

@Injectable({
  providedIn: 'root'
})

export class ManageUsersAccountService {
  userInfo: BehaviorSubject<BillingSummaryModel> = new BehaviorSubject<BillingSummaryModel>(null);
  clientSettings: BehaviorSubject<Settings[]> = new BehaviorSubject<Settings[]>([]);
  currentUserSubject: BehaviorSubject<JWTPayloadData> = new BehaviorSubject<JWTPayloadData>(null);
  userLoggedInSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  showAllItemsInToolbar$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  private api = this._utilService.getUserBaseUri();
  private authApi = this._utilService.getAuthorizationBaseUri();
  private _shiftJavaUri = this._utilService.getShiftJavaUri();
  private _auth = this._utilService.getAuthorizationBaseUri();

  constructor (
    private http: HttpClient,
    private _cookie: CookieService,
    private _utilService: UtilsService,
    private _brazeService: BrazeService
  ) {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    const clientSettings = JSON.parse(localStorage.getItem('clientSettings'));

    /**
     * Using window here instead of activated route since this constructor
     * runs before any routes are configured and can be read from (I tired)
     */
    if (!!user && !window.location.href.includes('clientid') && !window.location.href.includes('token')) {
      this.userLoggedInSubject.next(true);
      this.currentUserSubject.next(user);
    }

    if (!!clientSettings) {
      this.clientSettings.next(clientSettings);
    }
  }

  public get currentUserValue(): JWTPayloadData {
    return this.currentUserSubject.value;
  }

  public get fullName(): string {
    return `${this.currentUserSubject.value?.firstname} ${this.currentUserSubject.value?.lastname}`;
  }

  // if clienttype is not defined default to 3 (IntelyPro, the only live client type)
  public get facilityType(): number {
    return Number(this.clientSettings.value?.find(item => item.name === 'clienttype')?.value ?? 3);
  }

  // Saves the logged in user in ls
  public setCurrentUser(user: JWTPayloadData) {
    const userTypes = this.getUserTypesFromLS();

    if (userTypes.length) {
      user.usertypes = userTypes;
    }

    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  public getUserTypesFromLS(): UserTypeData[] {
    return JSON.parse(localStorage.getItem('userTypes')) as UserTypeData[] ?? [];
  }

  // Retrieve client settings
  public get clientSettingsGetter(): Settings[] {
    return this.clientSettings.value;
  }

  // Saves settings in LS
  public setClientSettings(settings: Settings[]) {
    localStorage.setItem('clientSettings', JSON.stringify(settings));
    this.clientSettings.next(settings);
  }

  // Updates the user profile name (from manage profile in settings)
  public updateUsername(firstname, lastname) {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    user.firstname = firstname;
    user.lastname = lastname;
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  sendRecoveryCode(email: string) {
    const requestBody = {
      'email': email
    };

    return this.http.post<BaseResponseModel<any>>(this.api + '/password/reset/requestemailcode', requestBody, httpOptions);
  }

  verifyRecoveryCode(code: string, email: string, password: string) {
    const requestBody = {
      'email': email,
      'code': code,
      'password': password
    };

    return this.http.put<JavaBaseResponse<any>>(this.authApi + '/user/password', requestBody, httpOptions);
  }

  login(email: string, password: string, userType: number) {
    const requestBody = {
      'email': email,
      'password': password,
      'defaultusertypes': [{ priority: '1', usertypeid: UserTypes.Scheduler }, { priority: '2', usertypeid: UserTypes.HubUser }],
      'productTypes': [2, 3, 4]
    };

    return this.http.post<BaseResponseModel<any>>(this.api + '/logon', requestBody);
  }

  getUserTypes(payload: any) {
    return this.http.get<BaseResponseModel<string>>(`${this._auth}/user/usertousertype/${payload?.userid}`).pipe(
      map(resp => resp?.data),
      catchError(() => of(JSON.stringify([payload?.jwtpayload.activeusertype]))),
      tap(userTypes => localStorage.setItem('userTypes', userTypes)),
      map(resp => JSON.parse(resp) as UserTypeData[])
    );
  }

  getPayLoad(handleFlag = true) {
    // For bearer tokens, populated in loginAsFacility()
    if (this.currentUserValue && this.currentUserValue['isBearer']) {
      return of({data: this.currentUserValue} as BaseResponseModel<JWTPayloadData>);
    }
    return this.http.get<BaseResponseModel<JWTPayloadData>>(this.api + '/payload', httpOptions).pipe(
      map(resp => resp.body),
      filter(payload => String(payload.code) === '200' && !!payload.data?.userid != null),
      tap(payload => {
        this.setCurrentUser(payload.data);
      })
    );
  }

  /**
   * Method to login with facility
   * @param clientid is facilityId
   * @param newToken  is JWT token
   * @param usertypeid is id
   * @returns http response
   */
  loginAsFacility(clientid: number, newToken: string, usertypeid, external = false) {
    if(newToken.includes("Bearer")) {
      // Create HttpParams for the POST request call. Setting the query params to camel case to satisfy endpoint.
      const params = new HttpParams()
        .set('userTypeId', usertypeid)
        .set('clientId', String(clientid));

      // Create HttpHeaders for the POST request call. This will add the bearer token to the header.
      const headers = new HttpHeaders().set('Authorization', newToken);

      return this.http.post<JavaBaseResponse<any>>(`${this.authApi}/client/linkschedulingtohub/hubToClientPlatformToken`, {}, {headers, params}).pipe(
        tap(response => {
          if(response.status === 'OK' && response.success == true) {
            // Change the fields for the jwtData to be lowercased.
            let jwtDataPayload = response.data.jwtData;

            // Changing the fields inside the JWT payload from camel case to lower case. This is for compatibilty reasons.
            function jwtPayloadToLowercase(object) {
              Object.keys(object).forEach((key) => {
                if (object[key] && typeof object[key] === 'object') jwtPayloadToLowercase(object[key]);
                object[key.toLowerCase()] = object[key];
              });
            }

            jwtPayloadToLowercase(jwtDataPayload);

            // We set "isBearer" to make sure we do not call the /payload API.
            jwtDataPayload['isBearer'] = true;

            // In the payload from the endpoint, all fields are camel cased.           e.g. "jwtCreated"
            // Though parsing the cookie values, all the fields are all lower cased.   e.g. "jwtcreated"
            const cookieDate = this.getJwtCreatedFromCookieValue(response.data.cookieValue, false);
            const refreshCookieDate = this.getJwtCreatedFromCookieValue(response.data.refreshCookieValue, true);
            if (external) {
              this.setCookieWithNullSameSite(response.data.cookieName, response.data.cookieValue, cookieDate);
              this.setCookieWithNullSameSite(response.data.refreshCookieName, response.data.refreshCookieValue, refreshCookieDate);
            } else {
              this._cookie.set(response.data.cookieName, response.data.cookieValue, cookieDate, '/');
              this._cookie.set(response.data.refreshCookieName, response.data.refreshCookieValue, refreshCookieDate, '/');
            }

            // Cookies must be written before setCurrentUser: emitting currentUserSubject lets
            // route guards activate components whose requests read the auth cookie (AG-1250)
            this.setCurrentUser(jwtDataPayload);
            this.userLoggedInSubject.next(true);
          }
        })
      );
    } else {
      const jd = moment(jwt_decode(newToken).jwtcreated).add(1, 'days').toDate();

      if (external) {
        this.setCookieWithNullSameSite('IntelycareAuthCookie', newToken, jd);
      } else {
        this._cookie.set('IntelycareAuthCookie', newToken, jd, '/');
      }

      return this.http.post<BaseResponseModel<any>>(`${this.api}/jwt/activeusertype`, {
        usertypeid,
        clientid
      }).pipe(
        tap(response => {
          if (Number(response.code) === 200) {
            const cookieDate = this.getJwtCreatedFromCookieValue(response.data.cookievalue, false);
            const refreshCookieDate = this.getJwtCreatedFromCookieValue(response.data.refreshcookievalue, true);
            if (external) {
              this.setCookieWithNullSameSite(response.data.cookiename, response.data.cookievalue, cookieDate);
              this.setCookieWithNullSameSite(response.data.refreshcookiename, response.data.refreshcookievalue, refreshCookieDate);
            } else {
              this._cookie.set(response.data.cookiename, response.data.cookievalue, cookieDate, '/');
              this._cookie.set(response.data.refreshcookiename, response.data.refreshcookievalue, refreshCookieDate, '/');
            }

            // Cookies must be written before setCurrentUser: emitting currentUserSubject lets
            // route guards activate components whose requests read the auth cookie (AG-1250)
            this.setCurrentUser(response.data.jwtpayload);
            this.userLoggedInSubject.next(true);
          }
        }),
        // Map the BaseResponseModel into a JavaBaseResponse model for compatibility sake.
        map(resp => {
          resp.data.jwtData = {...resp.data.jwtpayload};
          return {
            status: Number(resp.code) === 200 ? 'OK' : 'BAD_REQUEST',
            success: Number(resp.code) === 200,
            data: resp.data
          } as JavaBaseResponse<any>
        })
      );
    }
  }

  getJwtCreatedFromCookieValue(cookieValue: string, refreshCookie: boolean) {
    let jwtCreated = jwt_decode(cookieValue).jwtcreated;
    if(jwtCreated.includes(" +")) {
      jwtCreated = jwtCreated.split(" +")[0];
    }
    // If for refresh cookie, 2 days. If for normal cookie, only 1 day.
    return moment(jwtCreated).add(refreshCookie ? 2 : 1, 'days').toDate();
  }

  setCookieWithNullSameSite(cookieName: string, cookieValue: string, expirationDate, path = '/') {
    this._cookie.set(cookieName, cookieValue, expirationDate, path, null, true, 'None');
  }

  logout() {
    return this.http.post<BaseResponseModel<any>>(this.api + '/logoff', {}, httpOptions).pipe(
      map(response => {
        const loginscreen = this.getLoginScreen();
        this.clearLocalStorage();
        return loginscreen;
      }));
  }

  clearLocalStorage() {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('userTypes');
    localStorage.removeItem('intelypro');
    localStorage.removeItem('dow');
    localStorage.removeItem('clientSettings');
    localStorage.removeItem('openShifts-dayView');
    localStorage.removeItem('calendarViewType');
    localStorage.removeItem('calendarViewSelectedMonth');
    localStorage.removeItem('calendarViewStartDate');
    localStorage.removeItem('calendarViewEndDate');
    localStorage.removeItem('spreadsheetFilters');
    localStorage.removeItem('timecardFilters');
    localStorage.removeItem('monthly-sidepanel');
    localStorage.clear();
    sessionStorage.clear();
    this._cookie.delete('IntelycareAuthCookie', '/');
    this._cookie.delete('IntelycareRefreshCookie', '/');
    this._cookie.deleteAll();
    this.currentUserSubject.next(null);
  }

  // Based on user id landing page will be different
  navigateToLandingPage(userId: number): string {
    return '/dashboard';
  }

  getLoginScreen(): string {
    return '/facility/login';
  }

  getLoginUrlFromUserType(userTypeId: number): string {
    return '/facility/login';
  }

  getClientData(): Observable<BaseResponseModel<FacilityData>> {
    return this.http.get<BaseResponseModel<FacilityData>>(this.api + '/client');
  }

  set setDOW(dow: number) {
    localStorage.setItem('dow', String(dow));
  }

  get getDOW() {
    return !isNaN(Number(localStorage.getItem('dow'))) ? Number(localStorage.getItem('dow')) : 1;
  }

  initializeBraze() {
    // Only initialize braze if we have an identifiable user in the braze system
    this.getUserBrazeExternalId().subscribe(
      // If there is no external id, do not initialize braze
      (externalId) => { 
        if (!braze.isInitialized()) {
          braze.initialize('885f632d-7517-458b-9fc1-7faa6bbc5826', {
            baseUrl: 'sdk.iad-05.braze.com',
            allowUserSuppliedJavascript: true
          });
        }
        // Identify the user with the unique braze external id
        braze.changeUser(externalId);

        braze.automaticallyShowInAppMessages()

        braze.subscribeToContentCardsUpdates((cards: braze.ContentCards) => {
          this._brazeService.setContentCards(cards);
        });
        
        braze.openSession();
      }
    )
  }

  getUserBrazeExternalId(): Observable<string>  {
    return this.http.get<BaseResponseModel<string>>(`${this._auth}` + '/braze/user/external-id', {})
      .pipe(
        map((response: BaseResponseModel<string>) => response.data)
      );
  }

}
